import { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { usePermissions } from '@/hooks/use-permissions';
import { useAiSettings, useUpdateAiSettings } from '@/hooks';
import { buildDefaultAiSettings } from '@/lib/ai-settings-defaults';
import type { AiSettings } from '@/types';

export default function AiPlanPage() {
	const { data: settings } = useAiSettings();
	const updateMutation = useUpdateAiSettings();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');

	const [trialEnabled, setTrialEnabled] = useState<boolean>(false);
	const [savedTrialEnabled, setSavedTrialEnabled] = useState<boolean>(false);

	useEffect(() => {
		const value = settings?.aiPlan?.trialEnabled ?? false;
		setTrialEnabled(value);
		setSavedTrialEnabled(value);
	}, [settings]);

	const isDirty = trialEnabled !== savedTrialEnabled;

	const handleCancel = () => {
		setTrialEnabled(savedTrialEnabled);
	};

	const handleSave = async () => {
		const base = buildDefaultAiSettings(settings);
		const payload: AiSettings = {
			...base,
			aiPlan: { trialEnabled },
		};
		try {
			await updateMutation.mutateAsync(payload);
			setSavedTrialEnabled(trialEnabled);
			toast.success('AI Plan saved');
		} catch {
			toast.error('Failed to save AI Plan. Please try again.');
		}
	};

	return (
		<div className='flex flex-col w-full min-h-full'>
			{/* Page header */}
			<div className='sticky top-0 backdrop-blur-[6px] bg-white/80 border-b border-gray-500 flex items-center h-[60px] px-4 shrink-0 z-10'>
				<p className='text-base font-semibold text-ink leading-6'>AI Plan</p>
			</div>

			{/* Content */}
			<div className='flex flex-1 flex-col gap-6 p-4 bg-white'>
				<div className='w-[600px] border border-gray-500 rounded-[16px] overflow-hidden p-3 flex items-center gap-3'>
					<div className='flex-1 flex flex-col gap-1 min-w-0'>
						<p className='text-[13px] text-ink leading-[18px]'>Trial Plan</p>
						<p className='text-[12px] text-gray-800 leading-4'>
							The client is not on the full paid plan of AI
						</p>
					</div>
					<Switch
						checked={trialEnabled}
						onCheckedChange={(checked) => {
							if (canEdit) setTrialEnabled(checked);
						}}
						disabled={!canEdit}
					/>
				</div>
			</div>

			{/* Sticky footer — only visible when there are unsaved changes */}
			{isDirty && <div className='sticky bottom-0 border-t border-gray-300 bg-white px-4 py-3 flex items-center justify-end gap-2 shrink-0 z-10'>
				<button
					type='button'
					onClick={handleCancel}
					disabled={!isDirty || updateMutation.isPending}
					className={[
						'h-[30px] px-3 rounded-[20px] border border-gray-300 bg-white text-[12px] font-medium text-ink transition-colors',
						!isDirty || updateMutation.isPending
							? 'cursor-not-allowed opacity-50'
							: 'cursor-pointer hover:bg-gray-100',
					].join(' ')}
				>
					Cancel
				</button>
				<button
					type='button'
					onClick={handleSave}
					disabled={!isDirty || !canEdit || updateMutation.isPending}
					className={[
						'h-[30px] px-3.5 rounded-[100px] bg-ink border border-text-quote text-[12px] font-normal text-gray-50',
						!isDirty || !canEdit || updateMutation.isPending
							? 'cursor-not-allowed opacity-50'
							: 'cursor-pointer hover:bg-ink-strong',
					].join(' ')}
				>
					{updateMutation.isPending ? (
						<span className='flex items-center gap-2'>
							<span className='h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin' />
							Saving…
						</span>
					) : (
						'Save Changes'
					)}
				</button>
			</div>}
		</div>
	);
}
